import { Theme, Typography, useTheme } from "@mui/material"
import { HoldingDetailProps } from "../types"
import { Box } from "@mui/system"
import CustomTable from "../../common/customTable"
import { HoldingDetailsHeaderData } from "./tableHeaders"
import { HoldingDetailsBody } from "./tableBodies"
import HoldingDetailsLoader from "../holdingDetailsLoader"
import { GetSchemeDetailsResponseBody, SchemeDetail } from "../../../../redux-store/types/products"
import { useEffect, useState } from "react"
import { Order, PaginationObj, SortingObj } from "../../types"
import { useHistory } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { RootStateType } from "../../../../redux-store/reducers"
import { NO_DATA_AVAILABLE, USER_ROLES } from "../../../../utils/constant"
import { navigationData as NavigationData } from "../../types";
import { getSchemeDetails } from "../../../../redux-store/actions/product"
import { isRMLikeRole } from "../../../../utils/utilityFunctions"


interface FetchDataArgs {
    sortItem?: string;
    order?: Order;
    currentPage?: number;
    rowsPerPage?: number;
}

export const HoldingDetails = (): JSX.Element => {

    const history = useHistory();
    const dispatch = useDispatch();
    const { pathname: pathName, state } = history.location
    const navState = state as NavigationData
    const initialRouter = pathName.split('/').filter((item: string) => item !== '')
    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId = "", investorClientId = "" } = useSelector((store: RootStateType) => store.id);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData)
    const clientCode = isRMLikeRole(role) ? navState?.clientCode ?? '' : investorClientId;
    const [holdingDetailsData, setHoldingDetailsData] = useState<GetSchemeDetailsResponseBody>({} as GetSchemeDetailsResponseBody)
    const [tableData, setTableData] = useState<SchemeDetail[]>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [bondsholdingLoading, setBondsHoldingLoader] = useState<boolean>(false)
    const { palette } = useTheme()


    const fetchBondsHoldingDetails = async ({
        sortItem = "investedValue",
        order = "asc",
        currentPage = 1,
        rowsPerPage = 5
    }: FetchDataArgs = {}) => {
        try {
            setBondsHoldingLoader(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                sortItem,
                order,
                currentPage,
                rowsPerPage,
                clientCode,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'DB'
            }

            const holdingDetailRes = await (dispatch(getSchemeDetails(payload)) as unknown as Promise<GetSchemeDetailsResponseBody>)
            setHoldingDetailsData(holdingDetailRes)
            if (holdingDetailRes?.isSuccess) {
                setTableData(holdingDetailRes?.result || [])
                setTablePaginationObj(prevState => ({
                    ...prevState,
                    totalRows: holdingDetailRes?.totalLength
                }))
            } else {
                console.log(holdingDetailRes?.message)
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setBondsHoldingLoader(false)
        }
    }

    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "investedValue"
    })
    const handleSort = (orderBy: string, order: Order) => {
        setTableSortingObj({
            order,
            orderBy
        })

        fetchBondsHoldingDetails({
            sortItem: orderBy === 'gainLoss' ? 'unrealisedAmount' : orderBy,
            order,
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchBondsHoldingDetails({
            rowsPerPage: Number(event.target.value),
            sortItem: tableSortingObj.orderBy,
            order: tableSortingObj.order,
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchBondsHoldingDetails({
            currentPage: page + 1,
            sortItem: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    useEffect(() => {
        fetchBondsHoldingDetails()
    }, [])
    return (
        <Box sx={{
            padding: '25px 30px 45px 30px',
            background: palette.common.white,
            borderRadius: '20px',
            width: '100%',
        }}>
            {bondsholdingLoading && <HoldingDetailsLoader />}
            {(!bondsholdingLoading && !holdingDetailsData?.isSuccess) &&
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            }
            {(!bondsholdingLoading && holdingDetailsData?.isSuccess) &&

                <CustomTable
                    loadingRowsNum={4}
                    isLoading={false}
                    tableBody={
                        <HoldingDetailsBody
                            clientCode={clientCode}
                            tableBodyData={tableData || []}
                            handleCheckboxClick={(id: number) => console.log("Checkbox clicked", id)}
                        />
                    }
                    tableData={tableData || []}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeaderData={HoldingDetailsHeaderData}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px",
                        }
                    }}
                    handleSort={handleSort}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        }
                    }}
                    order={tableSortingObj.order}
                    orderBy={tableSortingObj.orderBy}
                    tableDataCount={tableData.length}
                    isPaginationVisible={true}
                    rowsPerPage={tablePaginationObj.rowsPerPage}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    handleRowsPerPageChange={handleRowsPerPageChange}
                    handlePageChange={handlePageChange}
                    currentPage={tablePaginationObj.currentPage}
                />
            }
        </Box >
    )
}